import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { type FC, useMemo, useState, useEffect } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { Link } from "@/components/flexkit/Link";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { Button } from "@/components/flexkit/Button";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { ArchivedEnvironmentsList } from "@/components/archive/ArchivedEnvironmentsList";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useEnvironmentsByProjects } from "@/hooks/use-environments-by-projects";
import {
    environmentsByProjectsToArchivedEnvironments,
    type ArchivedEnvironment,
    getEnvironmentDisplayName,
    getProjectDisplayName,
} from "@/utils/environment";
import { ArchivedEnvironmentDeleteModal } from "@/components/archive/ArchivedEnvironmentDeleteModal";
import { AutoDeleteSelect } from "@/components/archive/AutoDeleteSelect";
import { ArchivedEnvironmentSearchInput } from "@/components/archive/ArchivedEnvironmentSearchInput";
import { useDebounce } from "use-debounce";
import Fuse from "fuse.js";
import { useGroupedAgentExecutions } from "@/queries/agent-queries";
import { mostRecentAgentExecution } from "@/utils/agent-executions";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

type ArchivedSidebarProps = {
    onArchiveViewChange: (isArchive: boolean) => void;
};

export const ArchivedSidebarContent: FC<ArchivedSidebarProps> = ({ onArchiveViewChange }) => {
    const { data: environmentsByProjects, isLoading: isLoading } = useEnvironmentsByProjects({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ARCHIVED,
        includeAgentCreatedEnvironments: true,
    });

    const { data: groupedAgentExecutions } = useGroupedAgentExecutions();

    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 100, {});

    const archivedEnvironments = useMemo(
        () => (environmentsByProjects ? environmentsByProjectsToArchivedEnvironments(environmentsByProjects) : []),
        [environmentsByProjects],
    );

    // Filter environments based on search
    const filteredArchivedEnvironments = useMemo(() => {
        if (!debouncedSearchValue) {
            return archivedEnvironments;
        }

        // Add computed fields for search that match main sidebar naming
        const environmentsWithSearchFields = archivedEnvironments.map((env) => {
            const agentExecutions = groupedAgentExecutions?.environmentScoped.get(env.environment.id) ?? [];
            const agentExecution = mostRecentAgentExecution(agentExecutions);

            return {
                ...env,
                computedEnvironmentName: getEnvironmentDisplayName(env.environment, agentExecution),
                computedProjectName: getProjectDisplayName(env.project, env.projectMissing),
            };
        });

        const fuse = new Fuse(environmentsWithSearchFields, {
            includeScore: true,
            threshold: 0.3,
            distance: 100,
            minMatchCharLength: 2,
            keys: [
                "computedEnvironmentName",
                "computedProjectName",
                "environment.status.content.git.branch",
                "environment.metadata.name",
            ],
        });
        const result = fuse.search(debouncedSearchValue);

        return result.map((r) => r.item);
    }, [archivedEnvironments, debouncedSearchValue, groupedAgentExecutions]);

    const [environmentsToDelete, setEnvironmentsToDelete] = useState<ArchivedEnvironment[]>([]);

    const handleDeleteAll = () => {
        setEnvironmentsToDelete(filteredArchivedEnvironments);
    };

    const handleDeleteSingle = (environment: ArchivedEnvironment) => {
        setEnvironmentsToDelete([environment]);
    };

    useEscapeShortcut(() => {
        onArchiveViewChange(false);
    });

    return (
        <div className="flex size-full flex-col">
            <div className="px-2 pt-2.5">
                <SidebarHeader
                    onArchiveViewChange={onArchiveViewChange}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    hasArchivedEnvironments={archivedEnvironments.length > 0}
                />
            </div>
            <ScrollArea orientation="vertical" className="mr-0.5 flex-grow pt-2">
                <ArchivedEnvironmentsList
                    archivedEnvironments={filteredArchivedEnvironments}
                    isLoading={isLoading}
                    onDelete={handleDeleteSingle}
                    search={debouncedSearchValue}
                />
            </ScrollArea>
            <div className="pb-2">
                <SidebarFooter
                    archivedEnvironments={filteredArchivedEnvironments}
                    isLoading={isLoading}
                    onDeleteAll={handleDeleteAll}
                />
            </div>
            {environmentsToDelete.length > 0 && (
                <ArchivedEnvironmentDeleteModal
                    onClose={() => setEnvironmentsToDelete([])}
                    archivedEnvironments={environmentsToDelete}
                />
            )}
        </div>
    );
};

type SidebarHeaderProps = {
    onArchiveViewChange: (isArchive: boolean) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    hasArchivedEnvironments?: boolean;
};

const SidebarHeader: FC<SidebarHeaderProps> = ({
    onArchiveViewChange,
    searchValue,
    setSearchValue,
    hasArchivedEnvironments,
}) => {
    const { data: organization } = useOrganization();

    // Hide AutoDeleteSelect for FREE_ONA and CORE tiers
    const shouldShowAutoDelete = useMemo(() => {
        const tier = organization?.tier;
        return tier !== OrganizationTier.FREE_ONA && tier !== OrganizationTier.CORE;
    }, [organization?.tier]);

    return (
        <div className="flex flex-col">
            <div className="flex flex-col pb-4">
                <Tooltip
                    content={
                        <div className="flex flex-row items-center gap-2">
                            Archived environments
                            <KeyboardKey className="bg-surface-invert-tertiary">Esc</KeyboardKey>
                        </div>
                    }
                >
                    <Link
                        to="#"
                        className="flex h-8 flex-row items-center gap-1 rounded-lg pl-2 hover:bg-surface-03"
                        onClick={() => onArchiveViewChange(false)}
                        reloadIfFrontendUpdated={true}
                        data-tracking-id="archived-environments-sidebar"
                    >
                        <div className="w-5">
                            <IconArrowLeft size="base" />
                        </div>

                        <div className={cn("flex-grow truncate text-start text-base font-bold")}>
                            Archived environments
                        </div>
                    </Link>
                </Tooltip>
            </div>
            <div className="flex flex-col gap-2">
                <ArchivedEnvironmentSearchInput
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    disabled={!hasArchivedEnvironments}
                />
                {shouldShowAutoDelete && <AutoDeleteSelect />}
            </div>
        </div>
    );
};

type SidebarFooterProps = {
    archivedEnvironments: ArchivedEnvironment[];
    isLoading?: boolean;
    onDeleteAll: () => void;
};

const SidebarFooter: FC<SidebarFooterProps> = ({ archivedEnvironments, isLoading, onDeleteAll }) => {
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);

    useEffect(() => {
        if (archivedEnvironments.length > 0 && !isButtonEnabled) {
            const timer = setTimeout(() => {
                setIsButtonEnabled(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [archivedEnvironments.length, isButtonEnabled]);

    return (
        <div>
            {isLoading ? (
                <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                    <SkeletonBlock className="h-8 w-full" ready={false} />
                </div>
            ) : (
                archivedEnvironments.length > 0 && (
                    <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                        <Button
                            variant="destructive"
                            className="w-full"
                            onClick={onDeleteAll}
                            disabled={!isButtonEnabled}
                            data-tracking-id="delete-all-archived-environments-sidebar"
                        >
                            Delete All
                        </Button>
                    </div>
                )
            )}
        </div>
    );
};
